import type { ReactNode } from "react";
import { cn } from "../lib/utils";

interface PhoneFrameProps {
  /** Screenshot URL — a bundled asset from `../assets` (see `assets/index.ts`). */
  src: string;
  /** Alt text describing the screen for assistive tech. */
  alt: string;
  className?: string;
  /** Eager-load the hero frame; lazy-load the rest. */
  priority?: boolean;
}

/**
 * A CSS phone bezel that wraps a mobile screenshot so the marketing landing
 * page shows the real app the way people actually use it — on a handset. The
 * screenshots are captured at the same 390×844 logical viewport (see
 * `apps/web-e2e/tests/screenshots/mobile-screenshots.spec.ts`), so the
 * 9/19.5 aspect box frames them with no letterboxing.
 */
export function PhoneFrame({
  src,
  alt,
  className,
  priority = false,
}: PhoneFrameProps): ReactNode {
  return (
    <div
      className={cn(
        "relative aspect-[390/844] w-full rounded-[2.25rem] border border-white/60 bg-neutral-900 p-2 shadow-[0_30px_60px_-25px_rgba(38,0,110,0.55)] ring-1 ring-black/5",
        className,
      )}
    >
      {/* Speaker / camera notch. */}
      <div className="absolute left-1/2 top-3 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/25" />
      <div className="size-full overflow-hidden rounded-[1.65rem] bg-surface-container-lowest">
        <img
          src={src}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          className="size-full object-cover object-top"
        />
      </div>
    </div>
  );
}
